// Enhanced canvasFixer.js - Optimized for mobile and performance
const CanvasFixer = {
  // UNIFIED: Use centralized mobile detection
  get isMobile() { 
    return window.serviceLocator?.get('MobileDetectionService')?.isMobile() || 
           /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent); 
  },
  isIOS: /iPhone|iPad|iPod/i.test(navigator.userAgent),
  isAndroid: /Android/i.test(navigator.userAgent),
  isSafari: /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent),
  isFirefox: /Firefox/.test(navigator.userAgent),
  isEdge: /Edge/.test(navigator.userAgent) || /Edg\//.test(navigator.userAgent),
  
  // Fix canvas after tab switching
  fixCanvasAfterTabSwitch() {
    try {
      // Fix all canvases
      const canvases = document.querySelectorAll('canvas');
      canvases.forEach(canvas => {
        // Ensure canvas is visible and properly styled
        canvas.style.opacity = '1';
        canvas.style.filter = 'none';
        canvas.style.display = 'block';
        canvas.style.visibility = 'visible';
        
        // Force hardware acceleration
        canvas.style.transform = 'translateZ(0)';
        canvas.style.backfaceVisibility = 'hidden';
        canvas.style.perspective = '1000px';
        
        // Make focusable if not already
        if (!canvas.hasAttribute('tabindex')) {
          canvas.setAttribute('tabindex', '0');
        }
        
        // Focus the canvas
        try {
          canvas.focus();
        } catch (e) {
          console.warn("Error focusing canvas:", e);
        }
      });
      
      // Fix game container
      const gameContainer = document.getElementById('game-container');
      if (gameContainer) {
        gameContainer.style.opacity = '1';
        gameContainer.style.filter = 'none';
        gameContainer.style.visibility = 'visible';
      }
      
      // Force re-render active scene
      this.fixActiveScene();

      // Tell the active game scene to fix its game logic
      this.notifyGameSceneTabChange();
      
      return true;
    } catch (error) {
      console.warn("Error fixing canvas after tab switch:", error);
      return false;
    }
  },

  // Fix active scene helper method 
  fixActiveScene() {
    try {
      const gameInstance = window.gameInstance;
      if (!gameInstance || !gameInstance.getActiveScene) return false;
      
      const activeScene = gameInstance.getActiveScene();
      if (!activeScene) return false;
      
      // Reset camera properties
      if (activeScene.cameras && activeScene.cameras.main) {
        activeScene.cameras.main.alpha = 1;
        
        // If it's PPTHero scene, set background color
        if (activeScene.scene && activeScene.scene.key === 'PPTHeroScene') {
          activeScene.cameras.main.setBackgroundColor("#cccccc");
        }
      }
      
      // Bring scene to top
      if (activeScene.scene && typeof activeScene.scene.bringToTop === 'function') {
        activeScene.scene.bringToTop();
      }
      
      // Restart renderer if possible
      if (activeScene.game && activeScene.game.renderer && 
          typeof activeScene.game.renderer.reset === 'function') {
        activeScene.game.renderer.reset();
      }
      
      return true;
    } catch (error) {
      console.warn("Error fixing active scene:", error);
      return false;
    }
  },

// Notify game scene about tab change
// Notify game scene about tab change
notifyGameSceneTabChange() {
  try {
    const gameInstance = window.gameInstance;
    if (!gameInstance || !gameInstance.getActiveScene) return false;
    
    const activeScene = gameInstance.getActiveScene();
    
    // If the active scene has our visibility handler, call it
    if (activeScene && typeof activeScene.handleTabVisibilityChange === 'function') {
      activeScene.handleTabVisibilityChange();
    }
    
    // Additional handling for game scenes - check by scene key instead of instanceof
    if (activeScene && activeScene.scene && 
        (activeScene.scene.key === 'GameScene' || 
         activeScene.scene.key === 'MultiplayerGameScene' ||
         activeScene.scene.key === 'PPTHeroScene')) {
      
      // Call handleTabVisibilityChange if it exists
      if (typeof activeScene.handleTabVisibilityChange === 'function') {
        activeScene.handleTabVisibilityChange();
      }
      
      // Fix frozen asteroids if method exists
      if (typeof activeScene.checkForFrozenAsteroids === 'function') {
        activeScene.checkForFrozenAsteroids();
      }
    }
    
    return true;
  } catch (error) {
    console.warn("Error notifying game scene of tab change:", error);
    return false;
  }
},
  
  // Main canvas fixing method - enhanced for mobile
  fixCanvas() {
    try {
      // Fix all canvases
      const canvases = document.querySelectorAll('canvas');
      canvases.forEach(canvas => {
        // Ensure canvas is visible
        canvas.style.opacity = '1';
        canvas.style.filter = 'none';
        canvas.style.display = 'block';
        canvas.style.visibility = 'visible';
        
        // Make focusable if not already
        if (!canvas.hasAttribute('tabindex')) {
          canvas.setAttribute('tabindex', '0');
        }
        
        // Force hardware acceleration
        canvas.style.transform = 'translateZ(0)';
        canvas.style.backfaceVisibility = 'hidden';
        canvas.style.perspective = '1000px';
        
        // Mobile-specific enhancements
        if (this.isMobile) {
          canvas.style.touchAction = "pan-y"; // FIXED: Allow mobile scrolling
          canvas.style.webkitTapHighlightColor = "transparent";
          
          // iOS Safari specific fixes
          if (this.isIOS && this.isSafari) {
            canvas.style.position = 'absolute';
            canvas.style.width = '100%';
            canvas.style.height = '100%';
          }
        }
        
        // Browser-specific enhancements
        if (/Chrome/.test(navigator.userAgent)) {
          canvas.style.willChange = 'transform';
        }
      });
      
      // Fix game container
      const gameContainer = document.getElementById('game-container');
      if (gameContainer) {
        gameContainer.style.opacity = '1';
        gameContainer.style.filter = 'none';
        gameContainer.style.visibility = 'visible';
        
        // Mobile-optimized container positioning
        if (this.isMobile) {
          gameContainer.style.position = 'absolute';
          gameContainer.style.top = '50px'; // Account for ribbon
          gameContainer.style.left = '0';
          gameContainer.style.width = '100%';
          gameContainer.style.height = 'calc(100vh - 50px)';
          gameContainer.style.overflow = 'auto'; // FIXED: Allow mobile scrolling
          
          // Prevent background bleed-through
          gameContainer.style.backgroundColor = 'transparent';
          gameContainer.style.background = 'none';
        }
      }
      
      return true;
    } catch (e) {
      console.warn("Error fixing canvas:", e);
      return false;
    }
  },

    // Fix canvas and focus it for keyboard events
  fixAndFocusCanvas() {
    this.fixCanvas();
    
    // Focus the canvas for keyboard events
    try {
      const canvas = document.querySelector('canvas');
      if (canvas) {
        canvas.focus();
      }
    } catch (e) {
      console.warn("Error focusing canvas:", e);
    }
  },

  // Fix specific scene canvas - CRITICAL METHOD USED BY OTHER FILES
  fixSceneCanvas(scene) {
    if (!scene) return false;
    
    try {
      // Fix canvas first
      this.fixCanvas();
      
      // Reset scene cameras if they exist
      if (scene.cameras && scene.cameras.main) {
        scene.cameras.main.alpha = 1;
        scene.cameras.main.setBackgroundColor(scene.cameras.main.backgroundColor || "#000000");
      }
      
      // Bring scene to top
      if (scene.scene && typeof scene.scene.bringToTop === 'function') {
        scene.scene.bringToTop();
      }
      
      return true;
    } catch (e) {
      console.warn("Error fixing scene canvas:", e);
      return false;
    }
  },
  
  // Reset WebGL context if possible
  resetWebGLContext() {
    try {
      const canvas = document.querySelector('canvas');
      if (canvas) {
        // Get the WebGL context
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (gl && gl.getExtension('WEBGL_lose_context')) {
          // Force context refresh
          gl.getExtension('WEBGL_lose_context').loseContext();
          setTimeout(() => {
            gl.getExtension('WEBGL_lose_context').restoreContext();
            console.log("WebGL context has been reset");
          }, 100);
          return true;
        }
      }
      return false;
    } catch (e) {
      console.warn("Error resetting WebGL context:", e);
      return false;
    }
  },
  fixGameScene() {
    try {
        const gameInstance = window.gameInstance;
        let sceneToFix = null;

        // Try to get either game scene
        const gameScene = gameInstance?.scene?.getScene("GameScene");
        const multiplayerScene = gameInstance?.scene?.getScene("MultiplayerGameScene");

        if (gameScene && gameInstance.scene.isActive("GameScene")) {
            sceneToFix = gameScene;
        } else if (multiplayerScene && gameInstance.scene.isActive("MultiplayerGameScene")) {
            sceneToFix = multiplayerScene;
        }

        if (sceneToFix) {
            console.log(`Applying emergency fix to ${sceneToFix.scene.key}`);

            // --- ADD CHECKS ---
            if (sceneToFix.physics && sceneToFix.physics.world && sceneToFix.physics.world.isPaused) {
                console.log(`   Resuming physics for ${sceneToFix.scene.key}`);
                sceneToFix.physics.resume();
            } else if (!sceneToFix.physics || !sceneToFix.physics.world) {
                 console.warn(`   Cannot check/resume physics for ${sceneToFix.scene.key}, physics world missing.`);
            }
            // --- END CHECKS ---

             // Add other potential fixes specific to game scenes if needed
             if (typeof sceneToFix.checkForFrozenAsteroids === 'function') {
                 console.log(`   Checking for frozen asteroids in ${sceneToFix.scene.key}`);
                 sceneToFix.checkForFrozenAsteroids();
             }


            return true;
        } else {
            console.log("No active GameScene or MultiplayerGameScene found to fix.");
        }

        return false;
    } catch (error) {
        console.warn("Error in fixGameScene:", error);
        return false;
    }
},
  // Fix specifically for PPTHeroScene
  fixPPTHeroScene(pptHeroScene) {
    if (!pptHeroScene) return false;
    
    try {
      // Fix base canvas
      this.fixCanvas();
      
      // Reset camera color explicitly
      if (pptHeroScene.cameras && pptHeroScene.cameras.main) {
        pptHeroScene.cameras.main.alpha = 1;
        pptHeroScene.cameras.main.setBackgroundColor("#cccccc");
      }
      
      // Specific PPT Hero settings
      const gameContainer = document.getElementById("game-container");
      if (gameContainer) {
        gameContainer.style.filter = "none";
        gameContainer.style.opacity = "1";
        gameContainer.style.visibility = "visible";
        
        // Prevent background showing through
        gameContainer.style.backgroundColor = "transparent";
        gameContainer.style.background = "none";
      }
      
      // Ensure scene is active and properly rendered
      if (pptHeroScene.scene) {
        pptHeroScene.scene.bringToTop();
      }
      
      // Force re-render if method exists
      if (pptHeroScene.game && pptHeroScene.game.renderer && 
          typeof pptHeroScene.game.renderer.reset === 'function') {
        pptHeroScene.game.renderer.reset();
      }
      
      // Re-focus canvas
      const canvas = document.querySelector('canvas');
      if (canvas) {
        canvas.focus();
      }
      
      return true;
    } catch (e) {
      console.warn("Error fixing PPT Hero scene:", e);
      return false;
    }
  },
  
  // Aggressive fix that combines all approaches
  aggressiveFixPPTHero() {
    try {
      // Fix base canvas
      this.fixCanvas();
      
      // Reset WebGL context
      this.resetWebGLContext();
      
      // Fix game container specifically
      const gameContainer = document.getElementById("game-container");
      if (gameContainer) {
        gameContainer.style.filter = "none";
        gameContainer.style.opacity = "1";
        gameContainer.style.visibility = "visible";
        gameContainer.style.backgroundColor = "transparent";
        gameContainer.style.background = "none";
      }
      
      // Fix PPTHero scene if active
      const activeScene = window.gameInstance?.getActiveScene();
      if (activeScene && activeScene.scene && activeScene.scene.key === 'PPTHeroScene') {
        // Reset camera color
        if (activeScene.cameras && activeScene.cameras.main) {
          activeScene.cameras.main.alpha = 1;
          activeScene.cameras.main.transparent = false;
          activeScene.cameras.main.setBackgroundColor("#cccccc");
        }
        
        // Force scene to top
        if (activeScene.scene.bringToTop) {
          activeScene.scene.bringToTop();
        }
      }
      
      return true;
    } catch (e) {
      console.warn("Error in aggressiveFixPPTHero:", e);
      return false;
    }
  },
  
  // Start monitoring for canvas issues
  startMonitoring() {
    // Fix at regular intervals when PPTHero is active
    setInterval(() => {
      const gameInstance = window.gameInstance;
      if (!gameInstance) return;
      
      const activeScene = gameInstance.getActiveScene ? 
                          gameInstance.getActiveScene() : null;
                          
      if (activeScene && activeScene.scene && 
          activeScene.scene.key === 'PPTHeroScene') {
        this.fixCanvas();
      }
    }, 10000); // Check every 10 seconds
    
    // Watch for tab visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // Fix canvas when tab becomes visible again
        this.fixCanvasAfterTabSwitch();
      }
    });
    
    // Handle WebGL context loss
    window.addEventListener('webglcontextlost', (e) => {
      console.log('WebGL context lost, preventing default');
      e.preventDefault();
      
      // Fix canvas immediately and after a delay
      this.fixCanvas();
      setTimeout(() => this.fixCanvas(), 300);
    });
    
    window.addEventListener('webglcontextrestored', () => {
      console.log('WebGL context restored');
      this.fixCanvas();
    });
    // Add game-specific fixing to window visibility events
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    setTimeout(() => {
      CanvasFixer.fixGameScene();
    }, 300);
  }
});
    // Special handling for mobile
    if (this.isMobile) {
      // Handle orientation change
      window.addEventListener('orientationchange', () => {
        setTimeout(() => {
          this.fixCanvas();
          // Force resize event to update layout
          window.dispatchEvent(new Event('resize'));
        }, 300);
      });
      
      // REMOVED: This was blocking ALL touch scrolling on mobile
      // Touch scrolling is now handled by UnifiedMobileSystem
    }
    
    return true;
  },
  
  // Add emergency recovery UI for grey screens
  addEmergencyRecoveryUI() {
    // Create a global method to fix the canvas
    window.fixGameCanvas = () => {
      try {
        // Apply general canvas fixes
        this.fixCanvas();
        
        // If PPTHero scene is active, apply specific fixes
        const gameInstance = window.gameInstance;
        if (gameInstance) {
          const activeScene = gameInstance.getActiveScene ? 
                              gameInstance.getActiveScene() : null;
                              
          if (activeScene && activeScene.scene && 
              activeScene.scene.key === 'PPTHeroScene') {
            this.fixPPTHeroScene(activeScene);
          }
        }
        
        // Show success message
        alert("Canvas recovery attempted. If the screen is still grey, try refreshing the page.");
      } catch (e) {
        console.error("Error during canvas emergency recovery:", e);
        alert("Canvas recovery failed. Please try refreshing the page.");
      }
    };
    
    // Add a hidden button that becomes visible on grey screen detection
    const recoveryBtn = document.createElement('button');
    recoveryBtn.textContent = 'Fix Display Issues';
    recoveryBtn.style.position = 'fixed';
    recoveryBtn.style.bottom = '20px';
    recoveryBtn.style.right = '20px';
    recoveryBtn.style.zIndex = '10000';
    recoveryBtn.style.backgroundColor = '#FF3333';
    recoveryBtn.style.color = '#FFFFFF';
    recoveryBtn.style.border = 'none';
    recoveryBtn.style.borderRadius = '4px';
    recoveryBtn.style.padding = '10px';
    recoveryBtn.style.cursor = 'pointer';
    recoveryBtn.style.display = 'none';
    recoveryBtn.style.fontFamily = 'monospace';
    recoveryBtn.style.fontWeight = 'bold';
    recoveryBtn.onclick = window.fixGameCanvas;
    
    document.body.appendChild(recoveryBtn);
    
    // Periodically check for grey screens
    setInterval(() => {
      try {
        const canvas = document.querySelector('canvas');
        if (!canvas) return;
        
        // Simple check - try to get pixel data
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        try {
          // Try to read pixels - will fail if canvas is broken
          ctx.getImageData(0, 0, 1, 1);
          
          // If we get here, canvas is working, hide button
          recoveryBtn.style.display = 'none';
        } catch (e) {
          // Canvas might be broken, show recovery button
          recoveryBtn.style.display = 'block';
        }
      } catch (e) {
        // Ignore errors during checking
      }
    }, 10000); // Check every 10 seconds
    
    console.log("Emergency recovery UI added. A button will appear if display issues are detected.");
  },
  
  // Mobile-specific method that applies additional fixes
  applyMobileFixes() {
    if (!this.isMobile) return;
    
    try {
      // Get canvas and add mobile-specific styles
      const canvas = document.querySelector('canvas');
      if (canvas) {
        // Allow touch interactions for buttons, prevent scrolling/zooming only
        canvas.style.touchAction = 'pan-y'; // FIXED: Allow mobile scrolling
        
        // Prevent tap highlight
        canvas.style.webkitTapHighlightColor = 'transparent';
        
        // Make sure canvas fills the container
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        
        // Optimize rendering on iOS
        if (this.isIOS) {
          canvas.style.transform = 'translate3d(0,0,0)';
          
          // Get all parent elements and prevent scrolling
          let parent = canvas.parentElement;
          while (parent) {
            parent.style.overscrollBehavior = 'none';
            parent = parent.parentElement;
          }
        }
      }
      
      // Fix the game container
      const gameContainer = document.getElementById('game-container');
      if (gameContainer) {
        // Ensure it fills the available space below the ribbon
        gameContainer.style.position = 'absolute';
        gameContainer.style.top = '50px';
        gameContainer.style.left = '0';
        gameContainer.style.width = '100%';
        gameContainer.style.height = 'calc(100vh - 50px)';
        gameContainer.style.overflow = 'auto'; // Allow scrolling on mobile
        
        // Prevent scrolling
        gameContainer.style.overscrollBehavior = 'none';
      }
      
      // Optimize document/body styles for mobile
      document.documentElement.style.height = `${window.innerHeight}px`;
      document.body.style.height = `${window.innerHeight}px`;
      document.body.style.overscrollBehavior = 'none';
      
      // Prevent pinch zoom on iOS
      if (this.isIOS) {
        document.addEventListener('gesturestart', function(e) {
          e.preventDefault();
        });
      }
    } catch (error) {
      console.warn("Error applying mobile fixes:", error);
    }
  }
};

// Start monitoring when module is imported
CanvasFixer.startMonitoring();

// Add emergency recovery UI
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    CanvasFixer.addEmergencyRecoveryUI();
    
    // Apply mobile-specific fixes on load
    if (CanvasFixer.isMobile) {
      CanvasFixer.applyMobileFixes();
    }
  });

  // Add event listeners for WebGL context
  if (typeof window !== 'undefined') {
    // Handle WebGL context loss
    window.addEventListener('webglcontextlost', (e) => {
      console.log('WebGL context lost, preventing default');
      e.preventDefault();
      
      // Fix canvas immediately and after a delay
      setTimeout(() => CanvasFixer.aggressiveFixPPTHero(), 100);
    });
    
    window.addEventListener('webglcontextrestored', () => {
      console.log('WebGL context restored');
      setTimeout(() => CanvasFixer.aggressiveFixPPTHero(), 100);
    });
  }
}


// Make fix function globally available
window.fixCanvasDisplay = CanvasFixer.fixCanvas.bind(CanvasFixer);

export default CanvasFixer;